/* Notification Center — Hold Time category Target grid (RTCFR 2026-10-14, consolidated 2026-10-15).
 *
 * Used by BOTH the New Rule wizard → Target step and Rule Detail → Targets when
 * the rule's category is Hold Time (Result Exceedance keeps PermitTargetsGrid).
 * Columns, in order: Matrix | Test | Method | Hold Time | Alert | Warn Before.
 *
 * ONE row per test: Matrix, Test and Method appear once (vertically centred), and the
 * test's configured hold times — Before Prep and/or Before Analysis, read from Test Guide
 * via the live catalog, never hardcoded — are STACKED in the Hold Time, Alert and Warn
 * Before columns, each line aligned with its own controls. Each entry has its own Alert
 * toggle and its own Warn Before times: changing one never touches the other. A saved
 * entry whose hold time has since been removed is shown flagged, not hidden.
 *
 * The shared <AppDataGrid> gives the standard look, sorting, Excel-style column
 * filters on Matrix / Test / Method, paging and export; the search box above it
 * matches Matrix, Test or Method. */
import { useMemo, useState, type ReactNode } from 'react';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { markUserEdit } from '../../hooks/useUnsavedGuard';
import { WarnBeforeEditor } from './WarnBeforeEditor';
import {
  fmtWarnings, groupByTest, splitHoldTime, targetLabel, typeLabel,
  type HoldTimeCatalogRow, type HoldTimeEntry, type HoldTimeSel, type HoldTimeTestRow, type NcHoldTimeTarget,
} from './holdTime';

/** The stacked cell: one line per entry, each the same height so the three stacked
 *  columns stay aligned, with a subtle separator between lines. */
function Stack({ entries, render }: { entries: HoldTimeEntry[]; render: (e: HoldTimeEntry) => ReactNode }) {
  return (
    <div className="nc-ht-stack">
      {entries.map((e) => <div key={e.key} className="nc-ht-entry" data-type={e.holdTimeType}>{render(e)}</div>)}
    </div>
  );
}

export function HoldTimeTargetsGrid({
  catalog, saved, sel, onChange, readOnly = false, loading = false,
}: {
  /** Every configured hold time (one entry per test + type) — GET hold-time-catalog. */
  catalog: readonly HoldTimeCatalogRow[];
  /** The rule's saved targets (Rule Detail) — so an entry that lost its hold time is still shown. */
  saved?: readonly NcHoldTimeTarget[];
  /** htKey(test, type) → Alert flag + warnings (value + unit). A missing key = Alert off, no warnings. */
  sel: Record<string, HoldTimeSel>;
  onChange: (key: string, next: HoldTimeSel) => void;
  /** View mode: only the saved entries, toggles and warnings read-only. */
  readOnly?: boolean;
  loading?: boolean;
}) {
  const [q, setQ] = useState('');
  const all = useMemo(() => groupByTest(catalog, saved), [catalog, saved]);
  const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const visible = (readOnly
    ? all.map((r) => ({ ...r, entries: r.entries.filter((e) => sel[e.key]) })).filter((r) => r.entries.length > 0)
    : all)
    .filter((r) => tokens.every((t) => `${r.matrix} ${r.test} ${r.method}`.toLowerCase().includes(t)));
  const entryCount = all.reduce((n, r) => n + r.entries.length, 0);
  const onCount = all.reduce((n, r) => n + r.entries.filter((e) => sel[e.key]?.enabled).length, 0);
  // The Hold Time number column is exactly as wide as the longest value in the list (in
  // tabular-digit `ch`), so the numbers right-align under each other with no dead space
  // between the colon and a short value.
  const numChars = useMemo(() => Math.max(1, ...all.flatMap((r) => r.entries.map((e) => splitHoldTime(e.holdTime).value.length))), [all]);

  // Flips ONE entry's Alert; its warnings (and the other entry) are left as they are.
  const toggle = (e: HoldTimeEntry, el: Element) => {
    const cur = sel[e.key];
    onChange(e.key, { enabled: !(cur?.enabled ?? false), warn: cur?.warn ?? [] });
    markUserEdit(el);
  };

  const centre = () => 'nc-ht-vcenter';
  const columns: AppColumn<HoldTimeTestRow>[] = [
    { field: 'matrix', header: 'Matrix', width: '14%', cellClass: centre },
    // No width: the one AUTO column, so the table's auto layout gives it the spare width
    // instead of stretching the fixed Alert / Warn Before columns (2026-10-16).
    { field: 'test', header: 'Test', minWidth: 160, cellClass: centre },
    { field: 'method', header: 'Method', width: '14%', cellClass: centre },
    {
      field: 'holdTime', header: 'Hold Time', width: '20%', filterable: false,
      text: (r) => r.entries.map((e) => `${typeLabel(e.holdTimeType)}: ${e.holdTime}`).join('; '),
      // Sort by the real duration (shortest entry, in hours), not the label text.
      compare: (a, b) => Math.min(...a.entries.map((e) => e.holdHours ?? Number.MAX_VALUE))
                       - Math.min(...b.entries.map((e) => e.holdHours ?? Number.MAX_VALUE)),
      render: (r) => (
        <Stack entries={r.entries} render={(e) => {
          // Fixed sub-columns Label | : | Number | Unit ("Before Prep     : 24 hours"), so the
          // labels, the colons, the right-aligned numbers and the units line up on every line,
          // and Before Prep / Before Analysis read the same (RTCFR "Hold Time Alignment and Warn
          // Before Pop-up Size", 2026-10-15). The spaces between the cells are not rendered
          // (whitespace between grid items is dropped) but keep the text readable for screen
          // readers and copy: "Before Prep : 24 hours".
          const { value, unit } = splitHoldTime(e.holdTime);
          return (
            <span className={`nc-ht-hold${e.exists ? '' : ' nc-ht-missing'}`}
              title={e.exists ? undefined : 'Removed on Save — no longer configured in Test Guide.'}>
              <span className="nc-ht-type">{typeLabel(e.holdTimeType)}</span>{' '}
              <span className="nc-ht-colon" aria-hidden="true">:</span>{' '}
              <span className="nc-ht-num">{value}</span>{' '}
              <span className="nc-ht-unit">{unit}</span>
            </span>
          );
        }} />
      ),
    },
    {
      field: 'alert', header: 'Alert', width: 90, minWidth: 90, align: 'center', sortable: false, filterable: false,
      text: (r) => r.entries.map((e) => `${typeLabel(e.holdTimeType)} ${sel[e.key]?.enabled ? 'On' : 'Off'}`).join('; '),
      render: (r) => (
        <Stack entries={r.entries} render={(e) => {
          const on = sel[e.key]?.enabled === true;
          const label = `Alert — ${targetLabel({ ...r, holdTimeType: e.holdTimeType })}`;
          if (readOnly) return <span className={`nc-toggle${on ? ' on' : ''}`} role="img" aria-label={`${label}: ${on ? 'On' : 'Off'}`} />;
          if (!e.exists) return <span className={`nc-toggle${on ? ' on' : ''}`} aria-disabled="true" aria-label={label} />;
          return (
            <span className={`nc-toggle${on ? ' on' : ''}`} role="switch" aria-checked={on} tabIndex={0} aria-label={label}
              onClick={(ev) => { ev.stopPropagation(); toggle(e, ev.currentTarget); }}
              onKeyDown={(ev) => {
                if (ev.key !== ' ' && ev.key !== 'Enter') return;
                ev.preventDefault(); ev.stopPropagation();
                toggle(e, ev.currentTarget);
              }} />
          );
        }} />
      ),
    },
    {
      // Fixed 200px (was 22% ≈ 270px on a wide modal, owner request 2026-10-16): room for two
      // warning chips + "+N" + the dropdown arrow. It stays 200 because Test is the auto column.
      field: 'warn', header: 'Warn Before', width: 200, minWidth: 200, sortable: false, filterable: false,
      text: (r) => r.entries.map((e) => `${typeLabel(e.holdTimeType)} ${fmtWarnings(sel[e.key]?.warn ?? [])}`).join('; '),
      render: (r) => (
        <Stack entries={r.entries} render={(e) => (
          <WarnBeforeEditor
            values={sel[e.key]?.warn ?? []}
            holdHours={e.holdHours}
            holdTime={e.holdTime}
            label={targetLabel({ ...r, holdTimeType: e.holdTimeType })}
            readOnly={readOnly}
            disabled={!e.exists}
            onChange={(warn) => onChange(e.key, { enabled: sel[e.key]?.enabled ?? false, warn })}
          />
        )} />
      ),
    },
  ];

  return (
    <>
      {!readOnly && (
        <div className="nc-ht-toolbar">
          <div className="nc-search">
            <i className="ti ti-search" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-unsaved-ignore
              placeholder="Search Matrix, Test or Method…" aria-label="Search hold-time targets" />
          </div>
          <span className="nc-ht-count">{onCount} of {entryCount} hold times enabled · {all.length} tests</span>
        </div>
      )}
      {/* .nc-ht-grid: default arrow cursor instead of the shared grid's drag-scroll hand
          (owner request 2026-10-15); the Alert toggle and Warn Before keep the pointer. */}
      <div className="nc-ht-grid" style={{ ['--nc-ht-num-w' as string]: `${numChars}ch` }}>
      <AppDataGrid
        rows={visible}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        defaultPageSize={10}
        // No Export / Maximize strip above the headers (RTCFR "Hold Time Target Grid UI
        // Improvements", 2026-10-15): the headers sit right under the search row.
        // Sorting and the column filters are unaffected.
        hideHeaderTools
        emptyText={readOnly ? 'No hold-time targets.' : 'No tests with a configured hold time match.'}
      />
      </div>
    </>
  );
}
